<template>
  <section class="visit-filters" data-cy="visits-overview-filters-menu">
    <div class="visit-filters__actions">
      <input
        type="button"
        value="Close"
        class="filter-button-close"
        @click.prevent="emits('close')"
        data-cy="close-filters-button"
      />
      <input
        v-if="closedVisitsMode"
        type="button"
        value="Clear Date Range"
        class="filter-button-clear"
        @click.prevent="clearDateRange"
        data-cy="clear-filters-button"
      />
      <input
        type="button"
        value="Clear"
        class="filter-button-clear"
        @click.prevent="clearFilters"
        data-cy="clear-filters-button"
      />

      <input
        type="button"
        value="Apply"
        class="filter-button-apply"
        @click.prevent="applyFilters"
        data-cy="apply-filters-button"
      />
    </div>
    <div class="visit-filters__content">
      <ul class="visit-filters__list">
        <li v-if="closedVisitsMode" data-cy="filters-item">
          <label data-cy="filters-item-label">Begin Date</label>
          <DatePicker
            :max="new Date()"
            :value="beginDate"
            @change="emits('beginDateChanged', $event.value)"
          />
        </li>
        <li v-if="closedVisitsMode" data-cy="filters-item">
          <label data-cy="filters-item-label">End Date</label>
          <DatePicker
            :max="new Date()"
            :value="endDate"
            @change="emits('endDateChanged', $event.value)"
          />
        </li>
        <li v-for="item in filters" :key="item.label" data-cy="filters-item">
          <label data-cy="filters-item-label">{{ item.label }}</label>
          <DropDownList
            class="filters-dropdown"
            :data-items="item.values"
            v-model="item.value"
            data-cy="filters-item-dropdown"
          />
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from "vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import type { VisitDisplay } from "@/Domain/VisitDisplay";
import { DatePicker } from "@progress/kendo-vue-dateinputs";

const props = defineProps<{
  filters: FilterMenuItem[];
  getPropValues: (field: string) => Promise<VisitDisplay[]>;
  closedVisitsMode?: boolean;
  beginDate: Date;
  endDate: Date;
}>();

const emits = defineEmits<{
  (event: "apply", filters: FilterMenuItem[]): void;
  (event: "clear"): void;
  (event: "clearDateRange"): void;
  (event: "close"): void;
  (event: "beginDateChanged", date: Date): void;
  (event: "endDateChanged", date: Date): void;
}>();

const filters = ref([
  {
    field: "UrgentActionReq",
    label: "Urgent",
    placeholder: "Select Urgent",
    values: ["Yes", "No"] as string[],
    value: "Select Urgent",
  },
  {
    field: "Type",
    label: "Visit Type",
    placeholder: "Select Visit Type",
    values: [] as string[],
    value: "Select Visit Type",
  },
  {
    field: "ReportStatus",
    label: "Visit Status",
    placeholder: "Select Visit Status",
    values: [] as string[],
    value: "Select Visit Status",
  },
  {
    field: "Rc",
    label: "RC",
    placeholder: "Select RC",
    values: [] as string[],
    value: "Select RC",
  },
  {
    field: "RfrName",
    label: "RFR",
    placeholder: "Select RFR",
    values: [] as string[],
    value: "Select RFR",
  },
  {
    field: "PlantName",
    label: "Plant",
    placeholder: "Select Plant",
    values: [] as string[],
    value: "Select Plant",
  },
  {
    field: "Company",
    label: "Company Name",
    placeholder: "Select Company Name",
    values: [] as string[],
    value: "Select Company Name",
  },
] as FilterMenuItem[]);

const setFilterValues = () => {
  props.filters.forEach((f) => {
    const filter = filters.value.find((item) => item.field === f.field);
    if (filter) {
      filter.value = f.value;
    }
  });
};

setFilterValues();

const applyFilters = () => {
  const setFilters = filters.value.filter((f) => f.value !== f.placeholder);

  emits("apply", setFilters);
  emits("close");
};

const clearFilters = () => {
  emits("apply", []);
  emits("close");
};

const clearDateRange = () => {
  emits("clearDateRange");
  emits("close");
};

onBeforeMount(async () => {
  for (const filter of filters.value.filter((f) => f.values.length === 0)) {
    const values = await props.getPropValues(filter.field);
    filter.values = values.map((item) => (item as any)[filter.field]);
  }
});

watch(
  () => props.filters,
  () => {
    setFilterValues();
  }
);
</script>

<style lang="scss" scoped>
.visit-filters {
  position: absolute;
  background: #fff;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.3);
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 100;
  height: 100%;

  &__actions {
    padding: 15px;
    border-bottom: 1px solid #ddd;
    background: #efefef;
    justify-content: flex-end;
    display: flex;
    position: sticky;
    top: 0;
    z-index: 101;
    flex-wrap: wrap;
    gap: 8px;

    .filter-button-close,
    .filter-button-clear {
      text-align: center;
      padding: 8px 20px;
      box-shadow: none;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      background: 0 0;
      color: #3a5da6 !important;
      display: block;
    }

    .filter-button-close {
      margin-right: auto;
    }

    .filter-button-apply {
      text-align: center;
      color: #fff !important;
      padding: 8px 20px;
      box-shadow: none;
      background: #3a5da6;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      display: block;
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    padding-top: 10px;
  }

  &__list {
    padding-left: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    li {
      list-style-type: none;
    }

    label {
      font-weight: 500;
      font-size: 11px;
      text-transform: uppercase;
      color: #999;
      display: inline-block;
    }

    .filters-dropdown {
      width: 100%;
      margin-bottom: 8px;
    }
  }
}
</style>
